// eslint-disable-next-line max-classes-per-file
import { NavigateFunction } from 'react-router-dom'
import { EMkStepStatus, MkApiContext, TMkStep, ISelectOption } from '@typescript'
import { ProtectedDataStore } from '@utils/ProtectedDataStore'
import { DataStore, EMkStorageKeys } from '@utils/DataStore'
import { TMkResort } from '@search/services/searchTypes'
import { serverUrl } from '@utils/MkEnv'
import {
  ACCOUNT_TRIPS_PATH,
  API,
  CHECKIN_GUEST_SELECTED,
  CHECKIN_OWNER_SELECTED,
  MK_BOOK_PATH,
  RESORT_AVAILABILITY_BOOKING_ERROR_MESSAGE,
  TRAVELER_LABEL,
  UNEXPECTED_ERROR_OCCURRED,
} from '@constants'
import { buildApiRequestConfigFromStep } from '@utils/buildApiRequestConfigFromStep'
import { buildApiRequestUrl } from '@utils/buildApiRequestUrl'
import { formatCurrency } from '@utils/formattingUtils'
import {
  EMkBookStepName,
  ICost,
  IMkAnyBookField,
  IMkBookDraft,
  IMkBookState,
  IMkBookStepPaymentOptions,
  IMkBookStepTravelerInfo,
  IMkBookStepVipUpgrades,
  IMkBookTimerData,
  IMkTripSummary,
  MkBookAction,
  MkModifyBookStep,
  TMkAvailableSuite,
} from '../../types'
import * as actions from '../../store/_actions'
import { IBookService } from './IBookService'
import { formatBookDraftErrors } from './formatBookDraftErrors'

const initialState: IMkBookState = {
  bookDraft: undefined,
  isLoading: false,
  bookingErrors: [],
  timerData: undefined,
  originalTripSummary: undefined,
  apiContext: MkApiContext.BOOK_TRIP,
  requiresAdditionalPayment: false,
}

/** In-flight dedupe for modify init only (same key while request runs). Cleared when the request settles so leaving and re-entering a step refetches. */
const modificationInitPromises = new Map<string, Promise<void>>()

function modificationInitCacheKey(tripId: string, apiContext: MkApiContext, step: MkModifyBookStep | undefined): string {
  return `${tripId}::${apiContext}::${step ?? ''}`
}

function bookDraftHasResponseErrors(draft: IMkBookDraft | null | undefined): boolean {
  return Array.isArray(draft?.errors) && draft.errors.length > 0
}

class MkBookService implements IBookService {
  private state: IMkBookState

  private dispatch: React.Dispatch<MkBookAction>

  private navigate: NavigateFunction

  private authHeaders?: Record<string, string>

  constructor(state: IMkBookState, dispatch: React.Dispatch<MkBookAction>, navigate: NavigateFunction, authHeaders?: Record<string, string>) {
    this.state = state
    this.dispatch = dispatch
    this.navigate = navigate
    this.authHeaders = authHeaders
  }

  public static initialState(): IMkBookState {
    const bookDraft = MkBookService.pullBookDraft()
    const pulledTimerData = MkBookService.pullBookTimerData()
    const savedApiContext = MkBookService.pullApiContext()
    const timerData = bookDraft ? pulledTimerData || undefined : undefined

    if (!bookDraft && pulledTimerData) {
      MkBookService.removeBookTimerData()
    }

    return {
      ...initialState,
      bookDraft: bookDraft || undefined,
      timerData,
      apiContext: savedApiContext || MkApiContext.BOOK_TRIP,
    }
  }

  // Storage methods
  static pushBookDraft(draft: IMkBookDraft): void {
    try {
      ProtectedDataStore.setItem<IMkBookDraft>(EMkStorageKeys.MAKO_BOOK_SESSION, draft)
    } catch (error) {
      console.error('Failed to save booking steps to session:', error)
    }
  }

  static pushBookTimerData(timerData: IMkBookTimerData): void {
    try {
      DataStore.setItem<IMkBookTimerData>(EMkStorageKeys.MAKO_BOOK_TIMER, timerData)
    } catch (error) {
      console.error('Failed to save booking timer to session:', error)
    }
  }

  static removeBookTimerData(): void {
    try {
      DataStore.removeItem(EMkStorageKeys.MAKO_BOOK_TIMER)
    } catch (error) {
      console.error('Failed to remove booking timer from session:', error)
    }
  }

  private static pullBookDraft(): IMkBookDraft | undefined {
    try {
      const bookingDraftSession = ProtectedDataStore.getItem<IMkBookDraft>(EMkStorageKeys.MAKO_BOOK_SESSION)

      if (!bookingDraftSession || !bookingDraftSession.bookingSteps) {
        return undefined
      }
      return bookingDraftSession as IMkBookDraft
    } catch (error) {
      console.error('Failed to load booking steps from session:', error)
      return undefined
    }
  }

  private static pullBookTimerData(): IMkBookTimerData | undefined {
    try {
      const timerData = DataStore.getItem<IMkBookTimerData>(EMkStorageKeys.MAKO_BOOK_TIMER)
      return timerData || undefined
    } catch (error) {
      console.error('Failed to load booking timer from session:', error)
      return undefined
    }
  }

  private static pullApiContext(): MkApiContext | undefined {
    try {
      return DataStore.getItem<MkApiContext>(EMkStorageKeys.MAKO_API_CONTEXT) || undefined
    } catch (error) {
      console.error('Failed to load API context from storage:', error)
      return undefined
    }
  }

  private static pushApiContext(context: MkApiContext): void {
    try {
      DataStore.setItem<MkApiContext>(EMkStorageKeys.MAKO_API_CONTEXT, context)
    } catch (error) {
      console.error('Failed to save API context to storage:', error)
    }
  }

  // API methods
  private static async executeStepBookingRequest(
    currentBookStep: TMkStep | undefined,
    payload: Record<string, any>,
    apiContext: MkApiContext = MkApiContext.BOOK_TRIP,
    authHeaders?: Record<string, string>,
  ) {
    // Determine base URL based on apiContext
    const baseUrl = `${serverUrl}${API}/book`

    const { stepName, config: requestConfig } = buildApiRequestConfigFromStep(
      apiContext,
      currentBookStep,
      baseUrl,
      {
        accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Api-Context': apiContext,
        ...(authHeaders || {}),
      },
      payload,
    )

    const useQueryParams = requestConfig.method === 'GET' && !!requestConfig.params && Object.keys(requestConfig.params).length > 0
    const endpointToUse = buildApiRequestUrl(requestConfig.endpoint, { ...(useQueryParams ? requestConfig.params : {}) })
    const useBody = requestConfig.method !== 'GET' && requestConfig.body

    const response = await fetch(endpointToUse, {
      method: requestConfig.method,
      headers: { ...requestConfig.headers },
      ...(useBody && { body: JSON.stringify(requestConfig.body) }),
    })

    if (!response.ok) {
      throw new Error(UNEXPECTED_ERROR_OCCURRED)
    }

    return { data: (await response.json()) as IMkBookDraft, stepName }
  }

  private static getFieldLabelByName(step: TMkStep, targetValue: number | string): string {
    if (!step || !targetValue) return ''
    /* Search first for .value, as radio inputs share the same field name */
    const searchForRadio = Object.values(step.fieldSet).find((field) => field.value === targetValue) || null
    if (searchForRadio) {
      return searchForRadio.label
    }
    /* If no radios are found, search then for .name, as all other field types have a unique field name */
    const searchForOther = Object.values(step.fieldSet).find((field) => field.name === targetValue) || null
    if (searchForOther) {
      return searchForOther.label
    }
    return ''
  }

  getContextProps<T>(): T {
    const { bookDraft, timerData, originalTripSummary, isModifying, apiContext, requiresAdditionalPayment } = this.state
    const paymentCharges = bookDraft?.bookSummary?.paymentCharges || []
    const membershipCharges = bookDraft?.bookSummary?.membershipCharges || []
    const memberReimbursements = bookDraft?.bookSummary?.reimbursements?.member
    const paymentReimbursements = bookDraft?.bookSummary?.reimbursements?.payment
    const offerCodeEligible = bookDraft?.bookSummary?.offerCodeEligible || false
    const cancellationExpirationDate = bookDraft?.bookSummary?.cancellationExpirationDate || ''
    const cancellationPenaltyDeadlineLapsed = bookDraft?.bookSummary?.cancellationPenaltyDeadlineLapsed || false
    const totalPaymentDue = this.sumCosts(bookDraft?.bookSummary?.paymentCharges || [])
    const stay = bookDraft?.bookSummary?.stay || originalTripSummary?.stay
    const currentStep = this.getActiveStep()
    const rentalOptions = currentStep?.content?.rentalOptions ?? { rowHeaders: {}, columns: [] }
    const resort = bookDraft?.bookSummary?.resort || originalTripSummary?.resort
    const vipUpgradeFieldSet = (bookDraft?.bookingSteps.find(({ name }) => name === 'vipUpgrades')?.fieldSet || {}) as Record<string, IMkAnyBookField>
    const vipUpgradeNowFields = Object.values(vipUpgradeFieldSet).filter((field: any) => field.name === 'vipUpgradeNow')
    const vipUpgradeFutureFields = Object.values(vipUpgradeFieldSet).filter((field: any) => field.name === 'vipUpgradeFuture')
    const hasUpdatedModifySteps = this.state.hasUpdatedModifySteps ?? false
    return {
      bookService: this,
      ...this.state,
      apiContext,
      cancellationExpirationDate,
      cancellationPenaltyDeadlineLapsed,
      isModifying,
      membershipCharges,
      offerCodeEligible,
      originalTripSummary,
      paymentCharges,
      memberReimbursements,
      paymentReimbursements,
      rentalOptions,
      requiresAdditionalPayment,
      resort,
      stay,
      timerData,
      totalPaymentDue,
      vipUpgradeFutureFields,
      vipUpgradeNowFields,
      hasUpdatedModifySteps,
      applyModifications: this.applyModifications.bind(this),
      cancelBooking: this.cancelBooking.bind(this),
      cancelModifyReservation: this.cancelModifyReservation.bind(this),
      cancelModification: this.cancelModification.bind(this),
      checkStepStatus: IBookService.checkStepStatus,
      findStepIndexByName: this.findStepIndexByName.bind(this),
      findStepIndex: this.findStepIndexByName.bind(this),
      getActiveStep: this.getActiveStep.bind(this),
      getBookingTimerData: this.getBookingTimerData.bind(this),
      getCostById: this.getCostById.bind(this),
      getCostLabel: this.getCostLabel.bind(this),
      getFormattedCostAmount: this.getFormattedCostAmount.bind(this),
      getGuestConfirmationCost: this.getGuestConfirmationCost.bind(this),
      getOriginalReservation: this.getOriginalTripSummary.bind(this),
      getCreditsDeficit: this.getCreditsDeficit.bind(this),
      getPointsDeficit: this.getPointsDeficit.bind(this),
      getPurchaseGuestConfirmationCost: this.getPurchaseGuestConfirmationCost.bind(this),
      getReservationPointsDeficit: this.getReservationPointsDeficit.bind(this),
      getStayCreditsCost: this.getStayCreditsCost.bind(this),
      getStayPointsCost: this.getStayPointsCost.bind(this),
      getTravelerLabel: this.getTravelerLabel.bind(this),
      getUserBalanceById: this.getUserBalanceById.bind(this),
      handleStepComplete: this.handleStepComplete.bind(this),
      handleStepEdit: this.handleStepEdit.bind(this),
      handleStepInputFieldChange: this.handleStepInputFieldChange.bind(this),
      initBooking: this.initBooking.bind(this),
      initBookingTimer: this.initBookingTimer.bind(this),
      initModification: this.initModification.bind(this),
      isInModificationMode: this.isInModificationMode.bind(this),
      setBookingError: this.setBookingError.bind(this),
      setBookingErrors: this.setBookingErrors.bind(this),
      clearBookingErrors: this.clearBookingErrors.bind(this),
      setApiContext: this.setApiContext.bind(this),
      getApiContext: this.getApiContext.bind(this),
      setRequiresAdditionalPayment: this.setRequiresAdditionalPayment.bind(this),
      stopBookingTimer: this.stopBookingTimer.bind(this),
      clearBookingTimer: this.clearBookingTimer.bind(this),
      updateBookingTimer: this.updateBookingTimer.bind(this),
      updatePointSliderValues: this.updatePointSliderValues.bind(this),
      updateDatesStepContent: this.updateDatesStepContent.bind(this),
      updateBookDraft: this.updateBookDraft.bind(this),
    } as unknown as T
  }

  // New timer methods
  public initBookingTimer(durationMinutes: number, startTime?: number): void {
    const start = startTime ?? (new Date(this.state?.bookDraft?.meta?.createdAt).getTime() || Date.now())
    const timerData: IMkBookTimerData = {
      startTime: start,
      durationMinutes,
      remainingSeconds: durationMinutes * 60,
      isLastTwoMinutes: false,
      isTimeUp: false,
      stopped: false,
    }

    this.dispatch(actions.setTimerData(timerData) as MkBookAction)
  }

  public updateBookingTimer(remainingSeconds: number, isLastTwoMinutes: boolean, isTimeUp: boolean): void {
    const { timerData } = this.state

    if (!timerData) return

    const updatedTimerData: IMkBookTimerData = {
      ...timerData,
      remainingSeconds,
      isLastTwoMinutes,
      isTimeUp,
    }

    this.dispatch(actions.setTimerData(updatedTimerData) as MkBookAction)
  }

  public stopBookingTimer(): void {
    const { timerData } = this.state

    if (!timerData) return

    const updatedTimerData: IMkBookTimerData = {
      ...timerData,
      stopped: true,
    }

    this.dispatch(actions.setTimerData(updatedTimerData))
  }

  public clearBookingTimer(): void {
    if (!this.state.timerData) return

    this.dispatch(actions.clearTimerData())
  }

  public getBookingTimerData(): IMkBookTimerData | undefined {
    return this.state.timerData
  }

  /**
   * Look up the number of housekeeping credits cost
   */
  public getGuestConfirmationCost(): number {
    return this.getCostById('guest-confirmation')?.amount || 0
  }

  /**
   * Look up the cost to purchase a housekeeping credit
   */
  public getPurchaseGuestConfirmationCost(): number {
    return this.getCostById('purchase-guest-confirmation')?.amount || 0
  }

  /**
   * Look up the points cost of the book draft
   */
  public getStayPointsCost(): number {
    return this.getCostById('points')?.amount || 0
  }

  /**
   * Checks if the user has enough points for the stay, returns the deficit if not
   * @param userPointsBalance
   */
  public getPointsDeficit(userPointsBalance?: number): number {
    const stayPointsCost = this.getStayPointsCost()
    return userPointsBalance !== undefined && userPointsBalance < stayPointsCost ? stayPointsCost - userPointsBalance : 0
  }

  /**
   * Look up the vacation credits cost of the book draft
   */
  public getStayCreditsCost(): number {
    return this.getCostById('vacation-credits')?.amount || 0
  }

  /**
   * Checks if the user has enough credits for the stay, returns the deficit if not
   * @param userCreditsBalance
   */
  public getCreditsDeficit(userCreditsBalance?: number): number {
    const stayCreditsCost = this.getStayCreditsCost()
    return userCreditsBalance !== undefined && userCreditsBalance < stayCreditsCost ? stayCreditsCost - userCreditsBalance : 0
  }

  /**
   * Recursively search for a cost by its ID within the bookSummary cost structures.
   * @param costId
   */
  public getCostById(costId: string): undefined | ICost {
    const itemizedCostsArrays: (ICost[] | undefined)[] = [this.state.bookDraft?.bookSummary?.membershipCharges, this.state.bookDraft?.bookSummary?.paymentCharges]

    const findInItemized = (itemized: ICost[]): ICost | undefined =>
      itemized.reduce<ICost | undefined>((acc, itemizedCost) => {
        if (acc) return acc
        if (itemizedCost.id === costId) return itemizedCost
        if (itemizedCost.itemized) return findInItemized(itemizedCost.itemized)
        return undefined
      }, undefined)

    return itemizedCostsArrays.reduce<ICost | undefined>((found, costs) => {
      if (found) return found
      return costs ? findInItemized(costs) : undefined
    }, undefined)
  }

  /**
   * Returns the label value of a cost by its ID
   * @param costId
   */
  public getCostLabel(costId: string): string {
    if (!costId) return ''
    const cost = this.getCostById(costId)
    if (!cost) return ''
    return cost ? cost.label : ''
  }

  /**
   * Returns the formatted amount of a cost by its ID
   * @param costId
   */
  public getFormattedCostAmount(costId: string): string {
    if (!costId) return ''
    const cost = this.getCostById(costId)
    if (!cost) return ''
    if (cost.unit?.toLowerCase() === 'credits' || cost.unit?.toLowerCase() === 'points' || cost.unit === '') {
      // Format with commas and no decimals
      return formatCurrency(cost.amount, 0)
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: cost.unit?.toLowerCase(),
    }).format(cost.amount)
  }

  public getUserBalanceById(balanceId: string): number {
    if (this.state.bookDraft?.userBalances?.[balanceId]) {
      return +this.state.bookDraft.userBalances[balanceId]
    }
    return 0
  }

  // Add new API context methods
  public setApiContext(context: MkApiContext): void {
    this.dispatch(actions.setApiContext(context))
    MkBookService.pushApiContext(context)
  }

  public getApiContext(): MkApiContext {
    return this.state.apiContext
  }

  // Public methods
  // Using the static methods defined in the IMkBookService interface
  // No need to redefine them here

  findStepIndexByName(stepName: string): number {
    return this.state?.bookDraft?.bookingSteps.findIndex(({ name }) => name === stepName) || 0
  }

  getActiveStep(): TMkStep | undefined {
    return this.state?.bookDraft?.bookingSteps.find((step) => IBookService.checkStepStatus(step, EMkStepStatus.ACTIVE))
  }

  updateDatesStepContent(dates: { earliestCheckIn?: string; latestCheckOut?: string; excluded?: string[] }): void {
    this.dispatch(actions.updateDatesStepContent(dates) as MkBookAction)
  }

  updateBookDraft(bookDraft: IMkBookState['bookDraft']): void {
    this.dispatch(actions.setBookDraft(bookDraft as IMkBookDraft) as MkBookAction)
  }

  async handleStepComplete(): Promise<void> {
    const currentBookStep = this.getActiveStep()

    if (!currentBookStep) {
      throw new Error('No booking step found')
    }

    const { name: stepName } = currentBookStep

    this.dispatch(actions.setLoading(true))

    try {
      const { data: newBookDraft } = await MkBookService.executeStepBookingRequest(currentBookStep, this.state.bookDraft as IMkBookDraft, this.state.apiContext, this.authHeaders)

      // Update the book draft with the new data
      this.dispatch(
        actions.setBookDraft({
          ...newBookDraft,
        }) as MkBookAction,
      )

      // If the API returned errors for this step, surface them and do not proceed with step completion UI updates
      if (bookDraftHasResponseErrors(newBookDraft)) {
        // Parse error since it is string JSON coming from API
        const parsedErrors = newBookDraft.errors.map((error) => {
          try {
            const parsed = JSON.parse(error)
            // If successfully parsed and has a message, use it; otherwise treat as plain string
            return typeof parsed === 'object' && parsed.message ? parsed : { message: error, raw: error }
          } catch (e) {
            // If not JSON, treat the error string as the message directly
            return { message: error, raw: error }
          }
        })
        const firstError = parsedErrors[0]
        const errorMessage = firstError && typeof firstError.message === 'string' ? firstError.message : UNEXPECTED_ERROR_OCCURRED
        throw new Error(errorMessage)
      }

      // Find the corresponding completed step in the new draft
      if (currentBookStep && newBookDraft.bookingSteps) {
        const completedStep = newBookDraft.bookingSteps.find((step) => step.name === currentBookStep.name)
        if (completedStep) {
          this.setStepTitleCompleted(completedStep)
          if (completedStep.name === EMkBookStepName.MODIFY_TRIP_DATES) {
            this.dispatch(actions.setHasUpdatedModifySteps(true))
          }
        }
      }
    } catch (error) {
      console.error(`[${stepName}] Error during step completion`, error)
      this.setBookingError({
        raw: error as Error,
        message: typeof error === 'string' ? error : (error as any)?.message || UNEXPECTED_ERROR_OCCURRED,
      })
      throw new Error(typeof error === 'string' ? error : (error as any)?.message)
    } finally {
      this.dispatch(actions.setLoading(false))
    }
  }

  handleStepEdit(step: TMkStep): void {
    const stepIndex = this.findStepIndexByName(step.name)

    if (stepIndex === -1) {
      console.error(`Step ${step.name} not found in booking steps`)
      return
    }

    const updatedSteps = this.state?.bookDraft?.bookingSteps.map((currentStep, index) => {
      // Set the status based on the index relative to the target step
      let status
      if (index === stepIndex) {
        status = EMkStepStatus.ACTIVE
        this.resetStepTitleCompleted(currentStep)
      } else if (index < stepIndex) {
        status = EMkStepStatus.COMPLETE
      } else {
        status = EMkStepStatus.INCOMPLETE
      }

      return {
        ...currentStep,
        status,
      }
    }) as TMkStep[]

    // If we have a book draft, update it with the new steps
    if (this.state.bookDraft) {
      const updatedDraft = {
        ...this.state.bookDraft,
        bookingSteps: updatedSteps,
      }

      this.dispatch(actions.setBookDraft(updatedDraft as IMkBookDraft) as MkBookAction)
    }
  }

  async handleStepInputFieldChange(step: TMkStep, fieldName: string, fieldValue: IMkAnyBookField['value'], fieldType: string, opts?: { checked?: boolean }): Promise<void> {
    const finalFieldValue = fieldType === 'checkbox' && !opts?.checked ? '' : fieldValue

    this.dispatch(actions.updateField(step, fieldName, finalFieldValue, fieldType) as MkBookAction)
  }

  public initBooking(params: { resort: TMkResort; suite: TMkAvailableSuite; checkInDate: string; checkOutDate: string; vgerResortAvail?: any }): () => Promise<void> {
    return async (): Promise<void> => {
      if (!params || !params.resort || !params.suite || !params.checkInDate || !params.checkOutDate) {
        throw new Error('Invalid parameters for booking initialization')
      }

      this.dispatch(actions.setLoading(true))
      this.dispatch(actions.clearError())

      if (this.state.apiContext !== MkApiContext.BOOK_TRIP) {
        this.setApiContext(MkApiContext.BOOK_TRIP)
      }

      try {
        const { data: newBookDraft } = await MkBookService.executeStepBookingRequest(undefined, params, MkApiContext.BOOK_TRIP, this.authHeaders)

        this.dispatch(
          actions.setBookDraft({
            ...newBookDraft,
            // // TODO: This is a temporary. Remove after the API has been validated in Dev
            // bookSummary: {
            //   ...newBookDraft.bookSummary,
            //   resort: params.resort,
            //   stay: {
            //     ...newBookDraft.bookSummary.stay,
            //     suiteId: params.suite.id,
            //     suite: params.suite,
            //     suiteType: String(params.suite.type),
            //     checkIn: params.checkInDate,
            //     checkOut: params.checkOutDate,
            //     numberOfNights: Math.ceil((new Date(params.checkOutDate).getTime() - new Date(params.checkInDate).getTime()) / (1000 * 60 * 60 * 24)),
            //   },
            // },
          }),
        )

        const bookingStartTime = new Date(newBookDraft.meta?.createdAt).getTime() || Date.now()
        this.initBookingTimer(15, bookingStartTime)

        // If the API returned errors, surface them and do not navigate
        if (bookDraftHasResponseErrors(newBookDraft)) {
          throw new Error(formatBookDraftErrors(newBookDraft.errors))
        }

        this.navigate(MK_BOOK_PATH)
      } catch (error) {
        this.setBookingError({
          raw: error as Error,
          message: (error as any)?.message || RESORT_AVAILABILITY_BOOKING_ERROR_MESSAGE,
        })
        // Re-throw the error so the calling code can handle it
        throw error
      } finally {
        this.dispatch(actions.setLoading(false))
      }
    }
  }

  public isInModificationMode(): boolean {
    return !!this.state.isModifying
  }

  public getOriginalTripSummary(): IMkTripSummary | undefined {
    return this.state.originalTripSummary || undefined
  }

  public async initModification(params: { tripId: string; context?: MkApiContext | undefined; step?: MkModifyBookStep }): Promise<void> {
    const { tripId, context: apiContext = MkApiContext.MODIFY_TRIP_HUB, step } = params
    this.dispatch(actions.setHasUpdatedModifySteps(false))
    const cacheKey = modificationInitCacheKey(tripId, apiContext, step)

    const existing = modificationInitPromises.get(cacheKey)
    if (existing) {
      await existing
      return
    }

    const promise = this.performInitModification(params, cacheKey)
    modificationInitPromises.set(cacheKey, promise)
    await promise
  }

  private async performInitModification(params: { tripId: string; context?: MkApiContext | undefined; step?: MkModifyBookStep }, cacheKey: string): Promise<void> {
    const { tripId, context: apiContext = MkApiContext.MODIFY_TRIP_HUB, step } = params

    this.dispatch(actions.setLoading(true))

    // Set API context to modification mode
    this.setApiContext(apiContext)

    try {
      const { data: newBookDraft } = await MkBookService.executeStepBookingRequest(
        undefined,
        {
          confirmationNumber: tripId,
          modify: step,
        },
        apiContext,
        this.authHeaders,
      )

      if (!newBookDraft) {
        throw new Error('Failed to initialize modification.')
      }

      if (bookDraftHasResponseErrors(newBookDraft)) {
        throw new Error(formatBookDraftErrors(newBookDraft.errors))
      }

      // Update the book draft with the modification data
      this.dispatch(
        actions.setBookDraft({
          ...newBookDraft,
        }),
      )
    } catch (error) {
      console.error('[initModification] Error initializing modification:', error)
      this.setBookingError({
        raw: error as Error,
        message: (error as any)?.message || 'Failed to initialize modification',
      })
      this.navigate(`${ACCOUNT_TRIPS_PATH}/${tripId}/modify`)
    } finally {
      modificationInitPromises.delete(cacheKey)
      this.dispatch(actions.setLoading(false))
    }
  }

  public cancelModification(): void {
    modificationInitPromises.clear()
    this.dispatch(actions.setOriginalTripSummary(undefined as unknown as IMkTripSummary) as MkBookAction)
    this.dispatch(actions.setBookDraft(undefined as unknown as IMkBookDraft) as MkBookAction)
    this.clearBookingTimer()
    this.navigate(`${ACCOUNT_TRIPS_PATH}`)
  }

  public async cancelBooking(): Promise<void> {
    if (!this.state.bookDraft) {
      console.warn('No booking draft to cancel')
      return
    }

    this.dispatch(actions.setLoading(true))
    try {
      const { bookDraft } = this.state

      if (!bookDraft) {
        throw new Error('Cannot cancel booking: No active booking found')
      }

      const endpointToUse = buildApiRequestUrl(`${serverUrl}${API}/book/cancel`, {})

      try {
        // Call the cancel API endpoint
        const response = await fetch(endpointToUse, {
          method: 'POST',
          headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Api-Context': this.state.apiContext,
            ...(this.authHeaders || {}),
          },
          body: JSON.stringify(this.state.bookDraft),
        })

        if (!response.ok) {
          throw new Error(`Error canceling booking: ${response.status} ${response.statusText}`)
        }

        // Attempt to read response body for potential errors payload (bookDraft/errors)
        let cancelResponseData: any
        try {
          cancelResponseData = await response.json()
        } catch (_) {
          cancelResponseData = undefined
        }

        if (cancelResponseData && Array.isArray(cancelResponseData.errors) && cancelResponseData.errors.length > 0) {
          this.setBookingErrors(cancelResponseData.errors as any)
          return
        }

        // Clear the booking data
        this.dispatch(actions.setBookDraft(undefined as unknown as IMkBookDraft) as MkBookAction)

        this.clearBookingTimer()
      } catch (fetchError) {
        // Log the fetch error and rethrow it so the calling code can handle it
        console.error('Error during API call to cancel booking:', fetchError)
        throw fetchError
      }
    } catch (error) {
      console.error('Error canceling booking:', error)
      this.setBookingError({
        raw: error as Error,
        message: (error as any)?.message || 'Failed to cancel booking',
      })
      // Re-throw the error so the calling code can handle it
      throw error
    } finally {
      this.dispatch(actions.setLoading(false))
    }
  }

  public async cancelModifyReservation(): Promise<void> {
    this.dispatch(actions.setLoading(true))
    try {
      const endpointToUse = buildApiRequestUrl(`${serverUrl}${API}/book/modify/cancel`, {})

      try {
        // Call the cancel API endpoint
        const response = await fetch(endpointToUse, {
          method: 'POST',
          headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Api-Context': this.state.apiContext,
            ...(this.authHeaders || {}),
          },
          body: JSON.stringify(this.state.bookDraft),
        })

        if (!response.ok) {
          throw new Error(`Error canceling reservation: ${response.status} ${response.statusText}`)
        }

        this.dispatch(actions.setBookDraft(undefined as unknown as IMkBookDraft) as MkBookAction)
        this.dispatch(actions.setHasUpdatedModifySteps(false))
        this.clearBookingTimer()
      } catch (fetchError) {
        // Log the fetch error and rethrow it so the calling code can handle it
        console.error('Error during API call to cancel reservation:', fetchError)
        throw fetchError
      }
    } catch (error) {
      console.error('Error canceling reservation:', error)
      // Re-throw the error so the calling code can handle it
      throw error
    } finally {
      this.dispatch(actions.setLoading(false))
    }
  }

  public async applyModifications(): Promise<void> {
    if (!this.state.isModifying || !this.state.originalTripSummary) {
      throw new Error('Not in modification mode or no original reservation')
    }

    this.dispatch(actions.setLoading(true))
    try {
      const currentBookStep = this.getActiveStep()
      if (!currentBookStep) {
        throw new Error('No booking step found')
      }

      const { data: updatedTrip } = await MkBookService.executeStepBookingRequest(
        currentBookStep,
        {
          ...this.state.bookDraft,
          tripId: this.state.originalTripSummary?.tripId,
          isModification: true,
        },
        this.state.apiContext,
        this.authHeaders,
      )

      // If API returned errors during modification, surface them and stop
      if (Array.isArray(updatedTrip?.errors) && updatedTrip.errors.length > 0) {
        this.setBookingErrors(updatedTrip.errors as any)
        return
      }

      // Show confirmation and complete the modification
      this.dispatch(
        actions.setBookDraft({
          ...updatedTrip,
          bookSummary: {
            ...updatedTrip.bookSummary,
            resort: this.state.originalTripSummary?.resort || updatedTrip.bookSummary?.resort,
          },
        }),
      )

      // The completion handling will be done in the UI component
    } catch (error) {
      this.setBookingError({
        raw: error as Error,
        message: (error as any)?.message || 'Failed to apply modifications',
      })
    } finally {
      this.dispatch(actions.setLoading(false))
    }
  }

  // eslint-disable-next-line class-methods-use-this
  public getTravelerLabel(step: IMkBookStepTravelerInfo): string | undefined {
    if (step.formState.checkingIn === CHECKIN_OWNER_SELECTED) {
      // If there is only a single owner on the account, return the radio label
      let ownerLabel = step.fieldSet?.checkingInOwner?.label ?? ''

      // If there are multiple owners, find the label for the value chosen in the select
      if (step.formState.checkingInSelectedOwner !== undefined && step.formState.checkingInSelectedOwner !== '') {
        const availableOwners: ISelectOption[] = step.fieldSet?.checkingInSelectedOwner?.options || []
        const selectedOwnerId = step.formState?.checkingInSelectedOwner
        const selectedOwnerLabel = availableOwners.find((item) => item.value === selectedOwnerId)?.label?.trim()
        ownerLabel = selectedOwnerLabel ?? ''
      }
      if (ownerLabel) {
        return ownerLabel
      }
    }
    if (step.formState.checkingIn === CHECKIN_GUEST_SELECTED) {
      return `${step.formState.checkingInGuestFirstName} ${step.formState.checkingInGuestLastName}`
    }
    return undefined
  }

  setBookingError({ raw, message }: { raw?: Error; message?: string }): void {
    this.dispatch(
      actions.setError({
        raw,
        message: message || '',
        name: 'Booking Error',
      }),
    )
  }

  clearBookingErrors(): void {
    this.dispatch(actions.clearError())
  }

  setBookingErrors(errors: Array<{ message?: string; raw?: Error } | string>): void {
    this.dispatch(actions.clearError())
    errors.forEach((err) => {
      const message = typeof err === 'string' ? err : err?.message
      const raw = typeof err === 'string' ? undefined : err?.raw
      this.dispatch(
        actions.setError({
          raw: raw as any,
          message: message || '',
          name: 'Booking Error',
        } as any),
      )
    })
  }

  updatePointSliderValues(totalAmount: number, leadingValue: number, input1: IMkAnyBookField, input2: IMkAnyBookField): void {
    const activeStep = this.getActiveStep() as TMkStep
    this.handleStepInputFieldChange(activeStep, input1.name, leadingValue, input1.type)
    this.handleStepInputFieldChange(activeStep, input2.name, totalAmount - leadingValue, input2.type)
  }

  // eslint-disable-next-line class-methods-use-this
  getReservationPointsDeficit(): number {
    // Use static method since this method doesn't use 'this'
    return IBookService.getReservationPointsDeficit()
  }

  setRequiresAdditionalPayment(requiresAdditionalPayment: boolean): void {
    this.dispatch(actions.setRequiresAdditionalPayment(requiresAdditionalPayment))
  }

  private sumCosts(costs: ICost[]): number {
    return costs.reduce((total, cost) => {
      // skip if explicitly excluded
      if (cost.showInSummary === false) return total

      // if itemized exists, recurse into it instead of using parent amount
      if (cost.itemized && cost.itemized.length > 0) {
        return total + this.sumCosts(cost.itemized)
      }

      // otherwise take its own amount
      return total + cost.amount
    }, 0)
  }

  private updateStepCompletedTitleState<T = TMkStep>(step: T, title: string): void {
    this.handleStepInputFieldChange(step as TMkStep, 'titleCompleted', title, 'text')
  }

  private setStepTitleCompletedVipOptions(step: IMkBookStepVipUpgrades): void {
    const labelToUse = step.formState.vipUpgrade === 'now' ? 'Upgraded Suite' : MkBookService.getFieldLabelByName(step, step.formState.vipUpgrade)
    this.updateStepCompletedTitleState(step, labelToUse)
  }

  private setStepTitleCompletedTravelerInfo(step: IMkBookStepTravelerInfo): void {
    this.updateStepCompletedTitleState(step, this.getTravelerLabel(step) || TRAVELER_LABEL)
  }

  private setStepTitleCompletedPaymentOptions(step: IMkBookStepPaymentOptions): void {
    const label = MkBookService.getFieldLabelByName(step, step.formState.payWith)
    const stepTitle = label ? `Pay with ${label}` : 'Payment Options'
    this.updateStepCompletedTitleState(step, stepTitle)
  }

  // This method is implemented via static method using class-methods-use-this ESLint rule

  private resetStepTitleCompleted(step: TMkStep): void {
    this.updateStepCompletedTitleState(step, step.title)
  }

  private setStepTitleCompleted(step: TMkStep): void {
    if (!step) return
    switch (step?.name) {
      case EMkBookStepName.VIP_UPGRADES:
        this.setStepTitleCompletedVipOptions(step as IMkBookStepVipUpgrades)
        break
      case EMkBookStepName.TRAVELER_INFO:
        this.setStepTitleCompletedTravelerInfo(step as IMkBookStepTravelerInfo)
        break
      case EMkBookStepName.MODIFY_TRAVELER:
      case EMkBookStepName.MODIFY_TRIP_DATES:
      case EMkBookStepName.MODIFY_PROTECTION:
      case EMkBookStepName.MK_COMPLETE_MODIFICATION:
      case EMkBookStepName.MODIFY_TRIP:
        break
      case EMkBookStepName.PAYMENT_OPTIONS:
        this.setStepTitleCompletedPaymentOptions(step as IMkBookStepPaymentOptions)
        break
      case EMkBookStepName.RESERVATION_BALANCE:
      case EMkBookStepName.COMPLETE_BOOKING:
      default:
        console.info('[setStepTitleCompleted]', { stepName: step?.name, step })
    }
  }
}

export default MkBookService
